<template>
  <div class="body-wrapper">
    <div class="body-content gray">
      <div class="columns">
        <div class="column is-one-quarter"></div>
        <div class="column">
          <h1>Delete Account</h1>
          <div class="box">
            <p class="mb-4">
              Are you sure you want to delete your account? This cannot be
              undone.
            </p>
            <p class="mb-4">
              We retain the following information that may have been associated
              with your account for historical, patron service, and compliance
              purposes:
            </p>
            <ul class="mb-4 retention-list">
              <li>In progress and completed events</li>
              <li>Artist profile information</li>
              <li>Venue profile information</li>
              <li>Festival profile and past festival records</li>
              <li>Orders and order history information</li>
              <li>Patron and will call records</li>
              <li>Crash records and analytical data</li>
              <li>Booking and artist communication data</li>
            </ul>
            <div v-if="loading" class="has-text-centered">
              <progress class="progress is-primary" max="100"
                >Loading...</progress
              >
              <p>Checking your events...</p>
            </div>
            <div
              v-if="!loading && hasEventsInProgress"
              class="notification is-warning"
            >
              <p>
                You have events in progress that you are the sole user account
                on. We will be terminating these events and any payments for
                future performances will be refunded to patrons.
              </p>
            </div>
            <div v-if="error" class="notification is-danger">
              <p>{{ error }}</p>
            </div>
            <div class="buttons mt-5">
              <button class="button" @click="cancel">Cancel</button>
              <button
                class="button is-danger"
                :class="{ 'is-loading': deleting }"
                :disabled="loading || deleting"
                @click="deleteAccount"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
        <div class="column is-one-quarter"></div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";
import { mapGetters } from "vuex";
import store from "@/store";

export default {
  name: "DeleteAccount",
  data() {
    return {
      loading: true,
      deleting: false,
      hasEventsInProgress: false,
      error: null
    };
  },
  computed: {
    ...mapGetters({
      user: "user"
    }),
    adminAccountId() {
      // Exclude admin account from owner count
      // Production: vuX9yCXbJISNAvwE218arV7E8G72
      // Test: eJAQZaPVZCWd2ZzDdd1fZ5v34lD3
      return process.env.NODE_ENV === "production"
        ? "vuX9yCXbJISNAvwE218arV7E8G72"
        : "eJAQZaPVZCWd2ZzDdd1fZ5v34lD3";
    }
  },
  async mounted() {
    await this.checkEventsInProgress();
  },
  methods: {
    async checkEventsInProgress() {
      this.loading = true;
      try {
        const db = firebase.firestore();
        const userId = this.user.uid;

        // Query for published events where this user is an owner
        const eventsSnapshot = await db
          .collection("events")
          .where("owners", "array-contains", userId)
          .where("status", "==", "published")
          .get();

        // Check if user is the sole owner (excluding admin account)
        for (const doc of eventsSnapshot.docs) {
          const event = doc.data();
          const owners = event.owners || [];
          // Filter out the admin account
          const nonAdminOwners = owners.filter(
            ownerId => ownerId !== this.adminAccountId
          );
          // If user is the only non-admin owner
          if (nonAdminOwners.length === 1 && nonAdminOwners[0] === userId) {
            this.hasEventsInProgress = true;
            break;
          }
        }
      } catch (err) {
        console.error("Error checking events:", err);
        this.error = "Failed to check your events. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.$router.push({ name: "profile" });
    },
    async deleteAccount() {
      this.deleting = true;
      this.error = null;

      try {
        const db = firebase.firestore();
        const userId = this.user.uid;

        // Set deleted flag on user record
        await db
          .collection("users")
          .doc(userId)
          .update({
            deleted: true,
            deletedAt: firebase.firestore.FieldValue.serverTimestamp()
          });

        // Delete the Firebase Auth account
        const currentUser = firebase.auth().currentUser;
        await currentUser.delete();

        // Clear local state and redirect to home
        await store.dispatch("logOutUser");
        this.$router.push({ name: "home" });
      } catch (err) {
        console.error("Error deleting account:", err);

        if (err.code === "auth/requires-recent-login") {
          // Show login modal with reauth message
          store.dispatch("showReauthLogin");
        } else {
          this.error = "Failed to delete your account. Please try again.";
        }
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1rem;
}
.mt-5 {
  margin-top: 1.5rem;
}
.retention-list {
  list-style-type: disc;
  margin-left: 1.5rem;
}
</style>
